import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import {
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
  MAT_DIALOG_DATA,
  MatDialogRef,
} from '@angular/material/dialog';
import { DialogData } from '../../../../shared/models/dialog-data.model';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { MatRadioModule } from '@angular/material/radio';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { MatExpansionModule } from '@angular/material/expansion';
import { WebsiteBackupTarget } from '../../../../shared/models/backups.models';

@Component({
  selector: 'app-restore-backup-dialog',
  imports: [
    ReactiveFormsModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatButtonModule,
    MatCheckboxModule,
    FontAwesomeModule,
    MatRadioModule,
    FormsModule,
    MatExpansionModule,
  ],
  templateUrl: './restore-backup-dialog.component.html',
  styleUrl: './restore-backup-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RestoreBackupDialogComponent implements OnInit {
  readonly dialogRef = inject(MatDialogRef<RestoreBackupDialogComponent>);

  readonly data = inject<
    DialogData & {
      backupId: number;
      server: string;
      createdAt: string;
      websiteBackupId: number;
      backupTargets: WebsiteBackupTarget[];
      config: object;
    }
  >(MAT_DIALOG_DATA);

  private fb = inject(FormBuilder);

  readonly BUTTONS: { text: string; icon: IconProp }[] = [
    {
      text: 'Show what will be restored',
      icon: ['fas', 'eye'],
    },
    {
      text: 'Restore',
      icon: ['fas', 'arrows-rotate'],
    },
  ];

  readonly FEATURES: { value: string; label: string }[] = [
    {
      value: 'website',
      label: 'Website',
    },
    {
      value: 'databases',
      label: 'Databases',
    },
    {
      value: 'mailboxes',
      label: 'Mailboxes',
    },
    {
      value: 'dns',
      label: 'DNS',
    },
    {
      value: 'vhost',
      label: 'Origin Vhost',
    },
    {
      value: 'ssl',
      label: 'SSL',
    },
    {
      value: 'systemUser',
      label: 'SSH User',
    },
    {
      value: 'ftpUsers',
      label: 'FTP Users',
    },
    {
      value: 'cdn',
      label: 'CDN Vhost',
    },
  ];

  readonly PRESETS = [
    {
      value: 0,
      title: 'Restore Deleted Site',
      hint: 'Restore the site configuration, files, databases, and mailboxes.',
    },
    {
      value: 1,
      title: 'Custom Restore',
      hint: 'Choose which components and data to restore.',
    },
    {
      value: 2,
      title: 'Files and Database(s)',
      hint: 'Restore site root directory (files) and database(s).',
    },
  ] as const;

  form = this.fb.nonNullable.group({
    backupId: [0, [Validators.required]],
    websiteRestoreList: this.fb.nonNullable.array([
      this.fb.nonNullable.group({
        websiteBackupId: [0, [Validators.required]],
        restoreConfigTypes: this.fb.nonNullable.array<string>([]),
        restoreBackupTargets: this.fb.nonNullable.array<number>([]),
      }),
    ]),
  });

  preset = signal<0 | 1 | 2>(0);

  step = signal<0 | 1>(0);

  submitButton = computed(() => this.BUTTONS[this.step()]);

  disableFeatures = signal(false);

  disableTargets = signal(false);

  get selectedFeatures(): FormArray<FormControl<string>> {
    return this.form.controls.websiteRestoreList.controls[0].controls.restoreConfigTypes;
  }

  get selectedTargets(): FormArray<FormControl<number>> {
    return this.form.controls.websiteRestoreList.controls[0].controls.restoreBackupTargets;
  }

  get targets(): { value: number; label: string }[] {
    return this.data.backupTargets.map((t) => ({
      value: t.id,
      label: `[${t.snapshotTargetType}] ${t.snapshotTarget}`,
    }));
  }

  get rootDir(): { value: number; label: string } {
    const target = this.data.backupTargets.find((t) => t.type === 'vhost_document_root');
    return target
      ? { value: target.id, label: target.snapshotTarget }
      : { value: 0, label: 'Not found' };
  }

  get databases(): { value: number; label: string }[] {
    return this.data.backupTargets
      .filter((t) => t.type === 'database')
      .map((t) => ({
        value: t.id,
        label: t.snapshotTarget,
      }));
  }

  get mailboxes(): { value: number; label: string }[] {
    return this.data.backupTargets
      .filter((t) => t.type === 'mailbox')
      .map((t) => ({
        value: t.id,
        label: t.snapshotTarget,
      }));
  }

  get isAllFeatureSelected(): boolean {
    return this.selectedFeatures.length === this.FEATURES.length;
  }

  get isAllDatabasesSelected(): boolean {
    return this.databases.every((db) => this.selectedTargets.value.includes(db.value));
  }

  get isAllMailboxesSelected(): boolean {
    return this.mailboxes.every((mb) => this.selectedTargets.value.includes(mb.value));
  }

  get displayFeatures(): string[] {
    return this.FEATURES.filter((f) => this.selectedFeatures.value.includes(f.value)).map(
      (f) => f.label,
    );
  }

  get displayDatabases(): string[] {
    return this.databases
      .filter((db) => this.selectedTargets.value.includes(db.value))
      .map((db) => db.label);
  }

  get displayMailboxes(): string[] {
    return this.mailboxes
      .filter((mb) => this.selectedTargets.value.includes(mb.value))
      .map((mb) => mb.label);
  }

  get isSnapshotsToRestore(): boolean {
    return (
      this.selectedTargets.value.includes(this.rootDir.value) ||
      this.displayDatabases.length > 0 ||
      this.displayMailboxes.length > 0
    );
  }

  constructor() {
    effect(() => {
      const preset = this.preset();

      if (preset === 0) {
        this.selectAllFeatures();
        this.selectAllTargets();
        this.disableFeatures.set(true);
        this.disableTargets.set(true);
      } else if (preset === 1) {
        this.selectedFeatures.clear();
        this.selectFeaturesFromConfig();
        this.selectedTargets.clear();
        this.disableFeatures.set(false);
        this.disableTargets.set(false);
      } else if (preset === 2) {
        this.selectedFeatures.clear();
        this.selectAllTargets();
        this.disableFeatures.set(true);
        this.disableTargets.set(true);
      }
      this.form.updateValueAndValidity();
    });
  }

  ngOnInit(): void {
    this.form.controls.backupId.setValue(this.data.backupId);
    this.form.controls.websiteRestoreList.controls[0].controls.websiteBackupId.setValue(
      this.data.websiteBackupId,
    );
  }

  selectFeaturesFromConfig(): void {
    Object.keys(this.data.config).forEach((key) => {
      if (!this.selectedFeatures.value.includes(key)) {
        this.selectedFeatures.push(this.fb.control(key, { nonNullable: true }));
      }
    });
  }

  selectAllFeatures(): void {
    this.FEATURES.map((f) => f.value).forEach((key) => {
      if (!this.selectedFeatures.value.includes(key)) {
        this.selectedFeatures.push(this.fb.control(key, { nonNullable: true }));
      }
    });
  }

  selectAllTargets(): void {
    this.targets.forEach((target) => {
      if (!this.selectedTargets.value.includes(target.value))
        this.selectedTargets.push(this.fb.control(target.value, { nonNullable: true }));
    });
  }

  selectAllDatabases(): void {
    this.databases.forEach((database) => {
      if (!this.selectedTargets.value.includes(database.value))
        this.selectedTargets.push(this.fb.control(database.value, { nonNullable: true }));
    });
  }

  deselectAllDatabases(): void {
    for (let i = this.selectedTargets.length - 1; i >= 0; i--) {
      if (this.databases.map((d) => d.value).includes(this.selectedTargets.at(i).value)) {
        this.selectedTargets.removeAt(i);
      }
    }
  }

  toggleAllDatabases(selected: boolean): void {
    if (selected) this.selectAllDatabases();
    else this.deselectAllDatabases();
  }

  selectAllMailboxes(): void {
    this.mailboxes.forEach((mailbox) => {
      if (!this.selectedTargets.value.includes(mailbox.value))
        this.selectedTargets.push(this.fb.control(mailbox.value, { nonNullable: true }));
    });
  }

  deselectAllMailboxes(): void {
    for (let i = this.selectedTargets.length - 1; i >= 0; i--) {
      if (this.mailboxes.map((d) => d.value).includes(this.selectedTargets.at(i).value)) {
        this.selectedTargets.removeAt(i);
      }
    }
  }

  toggleAllMailboxes(selected: boolean): void {
    if (selected) this.selectAllMailboxes();
    else this.deselectAllMailboxes();
  }

  featuresToggle(): void {
    if (this.isAllFeatureSelected) this.selectedFeatures.clear();
    else this.selectAllFeatures();
  }

  onFeaturesChange(value: string, checked: boolean): void {
    const index = this.selectedFeatures.value.indexOf(value);

    if (checked && index === -1) {
      this.selectedFeatures.push(this.fb.control(value, { nonNullable: true }));
    }

    if (!checked && index !== -1) {
      this.selectedFeatures.removeAt(index);
    }
  }

  onTargetsChange(value: number, checked: boolean): void {
    const index = this.selectedTargets.value.indexOf(value);

    if (checked && index === -1) {
      this.selectedTargets.push(this.fb.control(value, { nonNullable: true }));
    }

    if (!checked && index !== -1) {
      this.selectedTargets.removeAt(index);
    }
  }

  submit(): void {
    if (this.step() === 0) {
      this.step.set(1);
      return;
    }
    if (this.form.invalid) return;
    this.dialogRef.close(this.form.getRawValue());
  }

  close(): void {
    this.dialogRef.close();
  }
}
